import React from 'react';
import { Check, X, FileText, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PersonalInfoRequest } from '@/types';
import { createBlobUrl } from '@/utils/files/conversion';

interface PersonalInfoRequestListProps {
  requests: PersonalInfoRequest[];
  isAdmin: boolean;
  onReview: (request: PersonalInfoRequest) => void;
  onReject: (requestId: string) => void;
}

export function PersonalInfoRequestList({ 
  requests, 
  isAdmin, 
  onReview, 
  onReject 
}: PersonalInfoRequestListProps) {
  if (requests.length === 0) {
    return <p className="p-4 text-center text-gray-500">لا توجد طلبات معلقة</p>;
  }

  return (
    <div className="bg-white shadow-sm rounded-lg divide-y">
      {requests.map((request) => {
        const photoUrl = request.personalInfo.photoUrl ? createBlobUrl(request.personalInfo.photoUrl) : null;
        const resumeUrl = request.personalInfo.resumeUrl ? createBlobUrl(request.personalInfo.resumeUrl) : null;

        return (
          <div key={request.id} className="p-4">
            <div className="flex justify-between">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
                    إضافة معلومات
                  </span>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    request.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    request.status === 'approved' ? 'bg-green-100 text-green-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {request.status === 'pending' ? 'معلق' : 
                     request.status === 'approved' ? 'مقبول' : 'مرفوض'}
                  </span>
                </div>

                <div className="text-sm text-gray-600 space-y-1">
                  <p>
                    <span className="font-medium ml-1">الاسم:</span>
                    {request.personalInfo.fullName}
                  </p>
                  <p>
                    <span className="font-medium ml-1">مقدم الطلب:</span>
                    {request.requesterName}
                  </p>
                  <p className="text-gray-500">{request.personalInfo.description}</p>
                </div>

                <div className="flex gap-4 mt-4">
                  {photoUrl && (
                    <div className="flex items-center gap-2">
                      <ImageIcon className="h-4 w-4 text-gray-400" />
                      <a 
                        href={photoUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline"
                      >
                        عرض الصورة
                      </a>
                    </div>
                  )}
                  {resumeUrl && (
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-gray-400" />
                      <a 
                        href={resumeUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline"
                      >
                        عرض السيرة الذاتية
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {request.status === 'pending' && isAdmin && (
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onReview(request)}
                    className="text-green-600 hover:text-green-700 hover:bg-green-50"
                    title="مراجعة الطلب"
                  >
                    <Check className="h-5 w-5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onReject(request.id)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    title="رفض الطلب"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}